import { useState, useEffect, useCallback } from 'react'
import { useGuestStore } from '../store'
import { apiClient } from '../services/apiClient'
import { extractErrorMessage } from '../utils/errorHandler'

const STATES_MAP: Record<string, {
  title: string
  description: string
  inputType: string
  placeholder?: string
}> = {
  CHOOSE_INPUT_METHOD: {
    title: 'Как заполнить данные?',
    description: 'Выберите удобный способ регистрации',
    inputType: 'choice',
  },

  WAIT_FULL_NAME: {
    title: 'ФИО',
    description: 'Введите фамилию, имя и отчество',
    inputType: 'text',
    placeholder: 'Иванов Иван Иванович'
  },

  WAIT_PASSPORT_NUMBER: {
    title: 'Номер паспорта',
    description: 'Введите серию и номер паспорта',
    inputType: 'text',
    placeholder: '4500 123456'
  },

  WAIT_PASSPORT_ISSUE_DATE: {
    title: 'Дата выдачи',
    description: 'Введите дату выдачи паспорта (ДД.ММ.ГГГГ)',
    inputType: 'text',
    placeholder: '15.03.2020'
  },

  WAIT_DATE_OF_BIRTH: {
    title: 'Дата рождения',
    description: 'Введите дату рождения (ДД.ММ.ГГГГ)',
    inputType: 'text',
    placeholder: '01.01.1990'
  },

  WAIT_BIRTH_PLACE: {
    title: 'Место рождения',
    description: 'Введите место рождения (город, область)',
    inputType: 'text',
    placeholder: 'г. Москва'
  },

  WAIT_ISSUE_PLACE: {
    title: 'Паспорт выдан',
    description: 'Введите орган и дату выдачи паспорта',
    inputType: 'text',
    placeholder: 'УФМС России по г. Москве'
  },

  WAIT_GENDER: {
    title: 'Пол',
    description: 'Укажите пол: М — мужской, Ж — женский',
    inputType: 'text',
    placeholder: 'М или Ж'
  },

  WAIT_ORGANIZATION_CODE: {
    title: 'Код подразделения',
    description: 'Введите код подразделения МВД, например 770-001',
    inputType: 'text',
    placeholder: '770-001'
  },

  WAIT_DOCUMENT_UPLOAD: {
    title: 'Фотография паспорта',
    description: 'Загрузите фотографию документа',
    inputType: 'file',
  },

  WAIT_OCR_PROCESSING: {
    title: 'Обрабатываем документ',
    description: 'Распознаём данные с фотографии паспорта',
    inputType: 'auto',
  },

  WAIT_OCR_CONFIRMATION: {
    title: 'Проверьте данные',
    description: 'Проверьте распознанную информацию перед продолжением',
    inputType: 'ocr_confirmation',
  },

  WAIT_RESIDENCE_ADDRESS: {
    title: 'Адрес регистрации',
    description: 'Введите адрес прописки',
    inputType: 'text',
    placeholder: 'г. Москва, ул. Примерная, д. 1, кв. 1'
  },

  WAIT_DOCUMENT_GENERATION: {
    title: 'Формируем документы',
    description: 'Подготавливаем документы для заселения',
    inputType: 'auto',
  },

  COMPLETED: {
    title: 'Регистрация завершена',
    description: 'Все данные успешно обработаны',
    inputType: 'success',
  }
}


export default function GuestRegistration() {

  const guest = useGuestStore((state) => state.guest)

  const currentState = useGuestStore(
    (state) => state.currentState
  )

  const setCurrentState = useGuestStore(
    (state) => state.setCurrentState
  )

  const stateData = useGuestStore(
    (state) => state.stateData
  )

  const setStateData = useGuestStore(
    (state) => state.setStateData
  )


  const [isLoading, setIsLoading] = useState(false)

  const [error, setError] = useState('')

  const [inputValue, setInputValue] = useState('')

  const [inputMethod, setInputMethod] = useState<
    'manual' | 'photo' | null
  >(null)

  const [initialized, setInitialized] = useState(false)

  const [showOCREditMode, setShowOCREditMode] =
    useState(false)

  const [ocrEditField, setOCREditField] =
    useState<string | null>(null)

  const [ocrEditedData, setOCREditedData] =
    useState<Record<string, string>>({})


  /*
   * Инициализация регистрации
   */

  useEffect(() => {

    const init = async () => {

      if (!guest || initialized) return

      try {

        const status =
          await apiClient.getGuestStatus(guest.id)

        setCurrentState(
          status.current_state ||
          'CHOOSE_INPUT_METHOD'
        )

        setStateData(
          status.data || {}
        )

        if (status.data?.input_method) {

          setInputMethod(
            status.data.input_method
          )

        }

      } catch (err) {

        console.error(
          'Failed to init:',
          err
        )

        setCurrentState(
          'CHOOSE_INPUT_METHOD'
        )

      }

      setInitialized(true)

    }

    init()

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [guest?.id])


  /*
   * Пропускаем загрузку документа
   * при ручном вводе
   */

  useEffect(() => {

    if (
      currentState === 'WAIT_DOCUMENT_UPLOAD' &&
      inputMethod === 'manual' &&
      guest?.id &&
      initialized
    ) {

      const skipDocumentUpload = async () => {

        try {

          setCurrentState(
            'WAIT_RESIDENCE_ADDRESS'
          )

        } catch (err) {

          console.error(
            'Error skipping document upload:',
            err
          )

        }

      }

      skipDocumentUpload()

    }

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [
    currentState,
    inputMethod,
    guest?.id,
    initialized
  ])


  /*
   * Запуск генерации документов
   */

  useEffect(() => {

    if (
      currentState !==
      'WAIT_DOCUMENT_GENERATION' ||
      !guest?.id ||
      !guest?.hotel_id
    ) {
      return
    }

    const triggerGeneration = async () => {

      try {

        const result =
          await apiClient.generateDocuments(
            guest.id,
            guest.hotel_id,
            [
              'contract',
              'receipt'
            ]
          )

        console.log(
          '📄 Document generation started:',
          result
        )

      } catch (err: any) {

        console.error(
          'Document generation error:',
          err
        )

      }

    }

    triggerGeneration()

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [
    currentState,
    guest?.id,
    guest?.hotel_id
  ])


  /*
   * Polling автоматических состояний
   */

  useEffect(() => {

    const isAutoState = [

      'WAIT_OCR_PROCESSING',

      'WAIT_DOCUMENT_GENERATION',

      'WAIT_APPROVAL'

    ].includes(currentState)


    if (
      !isAutoState ||
      !guest?.id ||
      !initialized
    ) {
      return
    }


    let pollCount = 0

    const maxPolls = 120


    const timer = setInterval(
      async () => {

        pollCount++

        try {

          const status =
            await apiClient.getGuestStatus(
              guest.id
            )


          setCurrentState(
            status.current_state
          )

          setStateData(
            status.data || {}
          )


          if (
            status.current_state !==
            currentState
          ) {

            clearInterval(timer)

          }


          if (
            pollCount >= maxPolls
          ) {

            clearInterval(timer)

          }

        } catch (err) {

          console.error(
            'Poll error:',
            err
          )

        }

      },
      1000
    )


    return () =>
      clearInterval(timer)

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [
    currentState,
    guest?.id,
    initialized
  ])


  /*
   * Отправка текстового шага
   */

  const handleSubmit = useCallback(
    async () => {

      if (
        !guest ||
        !currentState ||
        !inputValue.trim()
      ) {
        return
      }


      try {

        setError('')

        setIsLoading(true)

        // Накапливаем данные в stateData для ручного ввода
        const updatedData = { ...stateData }
        
        // Специальные маппинги для полей согласно шаблонам документов
        if (currentState === 'WAIT_FULL_NAME') {
          updatedData.full_name = inputValue.trim()
          updatedData.СОБСТВЕННИК_ФИО = inputValue.trim()
        } else if (currentState === 'WAIT_PASSPORT_NUMBER') {
          updatedData.passport_number = inputValue.trim()
          updatedData.СОБСТВЕННИК_ПАСПОРТ = inputValue.trim()
        } else if (currentState === 'WAIT_PASSPORT_ISSUE_DATE') {
          updatedData.passport_issue_date = inputValue.trim()
          updatedData.СОБСТВЕННИК_ПАСПОРТ_ВЫДАЧА_ДАТА = inputValue.trim()
        } else if (currentState === 'WAIT_DATE_OF_BIRTH') {
          updatedData.date_of_birth = inputValue.trim()
          updatedData.СОБСТВЕННИК_ПАСПОРТ_РОЖДЕНИЯ_ДАТА = inputValue.trim()
        } else if (currentState === 'WAIT_BIRTH_PLACE') {
          updatedData.birth_place = inputValue.trim()
          updatedData.СОБСТВЕННИК_МЕСТО_РОЖДЕНИЯ = inputValue.trim()
        } else if (currentState === 'WAIT_ISSUE_PLACE') {
          updatedData.issue_place = inputValue.trim()
          updatedData.СОБСТВЕННИК_ПАСПОРТ_ВЫДАЧА_МЕСТО = inputValue.trim()
        } else if (currentState === 'WAIT_GENDER') {
          updatedData.gender = inputValue.trim()
        } else if (currentState === 'WAIT_ORGANIZATION_CODE') {
          updatedData.organization_code = inputValue.trim()
          updatedData.СОБСТВЕННИК_ПАСПОРТ_КОД_ПОДРАЗ = inputValue.trim()
        } else if (currentState === 'WAIT_RESIDENCE_ADDRESS') {
          updatedData.residence_address = inputValue.trim()
        }

        setStateData(updatedData)

        await apiClient.submitStep(
          guest.id,
          currentState,
          inputValue.trim()
        )


        const status =
          await apiClient.getGuestStatus(
            guest.id
          )


        setCurrentState(
          status.current_state
        )

        setStateData(
          status.data || {}
        )

        setInputValue('')


      } catch (err: any) {

        setError(
          extractErrorMessage(err)
        )


      } finally {

        setIsLoading(false)

      }

    },

    [
      guest,
      currentState,
      inputValue,
      stateData
    ]

  )


  /*
   * Выбор способа регистрации
   */

  const handleChoice = async (
    choice: string
  ) => {

    if (
      !guest ||
      !currentState
    ) {
      return
    }


    try {

      setError('')

      setIsLoading(true)


      const submitRes =
        await apiClient.submitStep(
          guest.id,
          currentState,
          choice
        )


      console.log(
        'submitStep response:',
        submitRes
      )


      const status =
        await apiClient.getGuestStatus(
          guest.id
        )


      setCurrentState(
        status.current_state
      )

      setStateData(
        status.data || {}
      )


      if (
        status.current_state ===
        'WAIT_FULL_NAME' &&
        choice === 'manual'
      ) {

        setInputMethod(
          'manual'
        )

      } else if (
        status.current_state ===
        'WAIT_DOCUMENT_UPLOAD' &&
        choice === 'photo'
      ) {

        setInputMethod(
          'photo'
        )

      } else {

        setInputMethod(
          null
        )

      }


    } catch (err: any) {

      setError(
        extractErrorMessage(err)
      )


    } finally {

      setIsLoading(false)

    }

  }


  /*
   * Загрузка документа
   */

  const handleFileUpload = useCallback(

    async (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {

      const file =
        e.target.files?.[0]


      if (
        !file ||
        !guest
      ) {
        return
      }


      try {

        setError('')

        setIsLoading(true)


        const uploadRes =
          await apiClient.uploadDocument(
            guest.id,
            file
          )


        await apiClient.submitStep(
          guest.id,
          currentState,
          uploadRes.file_id
        )


        apiClient
          .processDocumentOCR(
            guest.id,
            uploadRes.file_id,
            guest.hotel_id
          )
          .catch(
            err =>
              console.error(
                'OCR error:',
                err
              )
          )


        const status =
          await apiClient.getGuestStatus(
            guest.id
          )


        setCurrentState(
          status.current_state
        )

        setStateData(
          status.data || {}
        )

        setInputMethod(null)


        e.target.value = ''


      } catch (err: any) {

        setError(
          extractErrorMessage(err)
        )


      } finally {

        setIsLoading(false)

      }

    },

    [
      guest,
      currentState
    ]

  )


  /*
   * Подтверждение OCR
   */

  const handleOCRConfirmation =
    useCallback(

      async (
        confirmedData:
          Record<string, string>
      ) => {

        if (!guest) {
          return
        }


        try {

          setError('')

          setIsLoading(true)

          // Добавляем маппинг для OCR данных к шаблонам
          const mappedData = { ...confirmedData }
          
          // Маппинг OCR полей на шаблонные переменные
          if (confirmedData.full_name) {
            mappedData.СОБСТВЕННИК_ФИО = confirmedData.full_name
          }
          if (confirmedData.passport_number) {
            mappedData.СОБСТВЕННИК_ПАСПОРТ = confirmedData.passport_number
          }
          if (confirmedData.date_of_birth) {
            mappedData.СОБСТВЕННИК_ПАСПОРТ_РОЖДЕНИЯ_ДАТА = confirmedData.date_of_birth
          }
          if (confirmedData.birth_place) {
            mappedData.СОБСТВЕННИК_МЕСТО_РОЖДЕНИЯ = confirmedData.birth_place
          }
          if (confirmedData.issue_place) {
            mappedData.СОБСТВЕННИК_ПАСПОРТ_ВЫДАЧА_МЕСТО = confirmedData.issue_place
          }
          if (confirmedData.issue_date) {
            mappedData.СОБСТВЕННИК_ПАСПОРТ_ВЫДАЧА_ДАТА = confirmedData.issue_date
          }
          if (confirmedData.organization_code) {
            mappedData.СОБСТВЕННИК_ПАСПОРТ_КОД_ПОДРАЗ = confirmedData.organization_code
          }

          const status =
            await apiClient.confirmOCRResults(
              guest.id,
              mappedData
            )


          setCurrentState(
            status.current_state
          )

          setStateData(
            status.data || {}
          )


          setShowOCREditMode(
            false
          )

          setOCREditedData(
            {}
          )

          setOCREditField(
            null
          )


        } catch (err: any) {

          setError(
            extractErrorMessage(err)
          )


        } finally {

          setIsLoading(false)

        }

      },

      [
        guest
      ]

    )


  /*
   * Состояния загрузки
   */

  if (!guest) {

    return (

      <div className="min-h-screen bg-[#f7f8fa] flex items-center justify-center px-4">

        <div className="rounded-xl border border-slate-200/80 bg-white px-6 py-8 text-center shadow-sm">

          <div className="text-3xl">
            🏨
          </div>

          <p className="mt-4 text-slate-600">

            Пожалуйста, начните регистрацию заново.

          </p>

        </div>

      </div>

    )

  }


  if (
    !currentState ||
    !initialized
  ) {

    return (

      <div className="min-h-screen bg-[#f7f8fa] flex items-center justify-center px-4">

        <div className="text-center">

          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200/80 border-t-blue-600" />

          <p className="mt-4 text-sm text-slate-500">

            Загружаем регистрацию...

          </p>

        </div>

      </div>

    )

  }


  /*
   * Прогресс
   */

  const stateInfo =
    STATES_MAP[currentState] ||
    STATES_MAP.CHOOSE_INPUT_METHOD


  const statesArray =
    Object.keys(
      STATES_MAP
    )


  const currentIndex =
    statesArray.indexOf(
      currentState
    )


  const progress =
    statesArray.length > 0 ? ((currentIndex + 1) / statesArray.length) * 100 : 0


  return (

    <div className="min-h-screen bg-[#f7f8fa]">


      {/* Верхняя панель */}

      <header className="border-b border-slate-200/80 bg-white/95 backdrop-blur">

        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-5">


          <div className="flex items-center gap-3">

            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-xl shadow-sm">

              🏨

            </div>


            <div>

              <div className="font-bold tracking-tight text-slate-900">

                HotelCheck

              </div>


              <div className="text-xs text-slate-500">

                Онлайн-регистрация

              </div>

            </div>

          </div>


        </div>

      </header>


      <main className="mx-auto max-w-2xl px-4 py-6 sm:py-10">


        {/* Прогресс */}

        <div className="mb-7">


          <div className="mb-2 flex items-center justify-between text-xs font-medium text-slate-500">

            <span>

              Прогресс регистрации

            </span>


            <span>

              {Math.round(progress)}%

            </span>

          </div>


          <div className="h-1.5 overflow-hidden rounded-full bg-slate-200">

            <div

              className="h-full rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 transition-all duration-500"

              style={{
                width: `${progress}%`
              }}

            />

          </div>

        </div>


        {/* Основная карточка */}

        <div className="overflow-hidden rounded-[28px] border border-slate-200/80 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.06)]">


          <div className="p-5 sm:p-8">


            {/* Заголовок */}

            <div className="mb-7">


              <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50/80 text-2xl">

                {stateInfo.inputType === 'choice' && '👋'}

                {stateInfo.inputType === 'text' && '📝'}

                {stateInfo.inputType === 'file' && '📄'}

                {stateInfo.inputType === 'auto' && '⚙️'}

                {stateInfo.inputType === 'ocr_confirmation' && '🔍'}

                {stateInfo.inputType === 'success' && '✓'}

              </div>


              <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">

                {stateInfo.title}

              </h1>


              <p className="mt-2 text-slate-500">

                {stateInfo.description}

              </p>

            </div>


            {/* Ошибка */}

            {error && (

              <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">

                <span>
                  ⚠️
                </span>

                <span>
                  {error}
                </span>

              </div>

            )}


            {/* Выбор способа */}

            {stateInfo.inputType === 'choice' && (

              <div className="space-y-3">


                <button

                  onClick={() =>
                    handleChoice('photo')
                  }

                  disabled={isLoading}

                  className="group w-full rounded-2xl border border-blue-600 bg-gradient-to-br from-blue-600 to-indigo-600 p-5 text-left text-white shadow-lg shadow-blue-600/15 transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-600/20 disabled:cursor-not-allowed disabled:opacity-60"

                >

                  <div className="flex items-center gap-4">


                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 text-2xl">

                      📸

                    </div>


                    <div className="flex-1">


                      <div className="font-semibold">

                        Загрузить фотографию паспорта

                      </div>


                      <div className="mt-1 text-sm text-blue-100">

                        Быстрый способ — система распознает данные автоматически

                      </div>

                    </div>


                    <div className="text-xl transition group-hover:translate-x-1">

                      →

                    </div>

                  </div>

                </button>


                <button

                  onClick={() =>
                    handleChoice('manual')
                  }

                  disabled={isLoading}

                  className="group w-full rounded-xl border border-slate-200/80 bg-white p-5 text-left text-slate-900 transition hover:border-blue-300 hover:bg-blue-50/80/50 disabled:cursor-not-allowed disabled:opacity-60"

                >

                  <div className="flex items-center gap-4">


                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-2xl">

                      ✍️

                    </div>


                    <div className="flex-1">


                      <div className="font-semibold">

                        Ввести данные вручную

                      </div>


                      <div className="mt-1 text-sm text-slate-500">

                        Заполните данные паспорта самостоятельно

                      </div>

                    </div>


                    <div className="text-xl text-slate-400 transition group-hover:translate-x-1">

                      →

                    </div>

                  </div>

                </button>


                <div className="mt-5 flex items-start gap-3 rounded-2xl bg-[#f7f8fa] p-4 text-sm text-slate-500">

                  <span>
                    🔒
                  </span>


                  <span>

                    Фотография документа используется для обработки данных и не хранится после распознавания.

                  </span>

                </div>

              </div>

            )}


            {/* Текстовое поле */}

            {stateInfo.inputType === 'text' && (

              <div className="space-y-4">


                <input

                  type="text"

                  value={inputValue}

                  onChange={(e) =>
                    setInputValue(
                      e.target.value
                    )
                  }

                  onKeyDown={(e) => {

                    if (
                      e.key === 'Enter'
                    ) {

                      handleSubmit()

                    }

                  }}

                  placeholder={
                    stateInfo.placeholder || stateInfo.description
                  }

                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"

                  disabled={isLoading}

                  autoFocus

                />


                <button

                  onClick={
                    handleSubmit
                  }

                  disabled={
                    isLoading ||
                    !inputValue.trim()
                  }

                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4 font-semibold text-white shadow-md shadow-blue-600/15 transition hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"

                >

                  {isLoading ? (

                    <>

                      <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />

                      Сохраняем...

                    </>

                  ) : (

                    <>

                      Продолжить

                      <span>
                        →
                      </span>

                    </>

                  )}

                </button>

              </div>

            )}


            {/* Ручной ввод в особом состоянии */}

            {inputMethod === 'manual' &&
              stateInfo.inputType === 'auto' && (

                <div className="space-y-4">


                  <input

                    type="text"

                    value={inputValue}

                    onChange={(e) =>
                      setInputValue(
                        e.target.value
                      )
                    }

                    onKeyDown={(e) => {

                      if (
                        e.key === 'Enter'
                      ) {

                        handleSubmit()

                      }

                    }}

                    placeholder={
                      stateInfo.description
                    }

                    className="w-full rounded-xl border border-slate-300 px-4 py-4 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"

                    disabled={isLoading}

                    autoFocus

                  />


                  <button

                    onClick={
                      handleSubmit
                    }

                    disabled={
                      isLoading ||
                      !inputValue.trim()
                    }

                    className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-4 font-semibold text-white shadow-md shadow-blue-600/15 transition hover:-translate-y-0.5 hover:shadow-lg disabled:bg-slate-300 disabled:shadow-none"

                  >

                    {isLoading
                      ? 'Сохраняем...'
                      : 'Продолжить →'}

                  </button>

                </div>

              )}


            {/* Загрузка документа */}

            {stateInfo.inputType === 'file' && (

              <div className="space-y-5">


                <label className="group block cursor-pointer rounded-3xl border-2 border-dashed border-slate-300 bg-[#f7f8fa] p-8 text-center transition hover:border-blue-500 hover:bg-blue-50/80 sm:p-12">


                  <input

                    type="file"

                    onChange={
                      handleFileUpload
                    }

                    accept=".png,.jpg,.jpeg,.pdf"

                    disabled={isLoading}

                    className="hidden"

                  />


                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-100/80 text-3xl transition group-hover:scale-105">

                    📄

                  </div>


                  <p className="mt-5 font-semibold text-slate-900">

                    Загрузите фотографию паспорта

                  </p>


                  <p className="mt-2 text-sm text-slate-500">

                    Нажмите, чтобы выбрать файл

                  </p>


                  <p className="mt-4 text-xs text-slate-400">

                    JPG, JPEG, PNG или PDF · до 10 МБ

                  </p>

                </label>


                <div className="flex items-start gap-3 rounded-xl bg-blue-50/80 p-4 text-sm text-blue-700">

                  <span>
                    🔒
                  </span>


                  <span>

                    Документ используется для автоматического распознавания данных.

                  </span>

                </div>


                {isLoading && (

                  <div className="flex items-center justify-center gap-3 text-sm font-medium text-blue-600">

                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />

                    Загружаем документ...

                  </div>

                )}

              </div>

            )}


            {/* Загрузка фото в нестандартном состоянии */}

            {inputMethod === 'photo' &&
              stateInfo.inputType !== 'file' &&
              stateInfo.inputType !== 'text' && (

                <div className="space-y-5">


                  <label className="group block cursor-pointer rounded-3xl border-2 border-dashed border-slate-300 bg-[#f7f8fa] p-8 text-center transition hover:border-blue-500 hover:bg-blue-50/80 sm:p-12">


                    <input

                      type="file"

                      onChange={
                        handleFileUpload
                      }

                      accept=".png,.jpg,.jpeg,.pdf"

                      disabled={isLoading}

                      className="hidden"

                    />


                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-100/80 text-3xl">

                      📷

                    </div>


                    <p className="mt-5 font-semibold text-slate-900">

                      Загрузите фотографию паспорта

                    </p>


                    <p className="mt-2 text-sm text-slate-500">

                      Выберите изображение документа

                    </p>

                  </label>


                  {isLoading && (

                    <div className="flex items-center justify-center gap-3 text-sm font-medium text-blue-600">

                      <span className="h-5 w-5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />

                      Загружаем документ...

                    </div>

                  )}

                </div>

              )}


            {/* Автоматическая обработка */}

            {stateInfo.inputType === 'auto' && (

              <div className="py-8 text-center">


                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-blue-50/80">


                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />

                </div>


                <h3 className="mt-6 text-lg font-semibold text-slate-900">

                  {stateInfo.title}

                </h3>


                <p className="mt-2 text-sm text-slate-500">

                  {stateInfo.description}

                </p>


                <div className="mx-auto mt-6 max-w-xs rounded-full bg-slate-100 px-4 py-2 text-xs text-slate-500">

                  Пожалуйста, не закрывайте страницу

                </div>

              </div>

            )}


            {/* Подтверждение OCR */}

            {stateInfo.inputType === 'ocr_confirmation' &&
              stateData?.ocr_results && (

                <div className="space-y-6">


                  <div className="rounded-xl border border-blue-200 bg-blue-50/80 p-5">


                    <div className="flex items-start gap-3">


                      <div className="text-xl">
                        ✓
                      </div>


                      <div>


                        <p className="font-semibold text-blue-900">

                          Данные распознаны

                        </p>


                        <p className="mt-1 text-sm leading-6 text-blue-700">

                          Проверьте информацию перед продолжением. При необходимости данные можно отредактировать.

                        </p>


                        {stateData.ocr_results.confidence && (

                          <p className="mt-2 text-xs text-blue-600">

                            Уверенность распознавания:

                            {' '}

                            {Math.round(
                              stateData.ocr_results.confidence * 100
                            )}

                            %

                          </p>

                        )}

                      </div>

                    </div>

                  </div>


                  {!showOCREditMode ? (

                    <>


                      <div>


                        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">

                          Распознанные данные

                        </h3>


                        <div className="space-y-2">


                          {stateData.ocr_results.extracted_fields &&

                            Object.entries(
                              stateData.ocr_results.extracted_fields
                            ).map(
                              ([key, value]) => (

                                <div

                                  key={key}

                                  className="flex items-center gap-4 rounded-xl border border-slate-200/80 bg-[#f7f8fa] p-4"

                                >


                                  <div className="min-w-0 flex-1">


                                    <p className="text-xs font-medium capitalize text-slate-500">

                                      {key.replace(
                                        /_/g,
                                        ' '
                                      )}

                                    </p>


                                    <p className="mt-1 break-words font-semibold text-slate-900">

                                      {String(value)}

                                    </p>

                                  </div>


                                  <button

                                    onClick={() => {

                                      setOCREditField(
                                        key
                                      )

                                      setShowOCREditMode(
                                        true
                                      )

                                    }}

                                    className="rounded-xl bg-white px-3 py-2 text-sm font-medium text-blue-600 shadow-sm transition hover:bg-blue-50/80"

                                  >

                                    Изменить

                                  </button>

                                </div>

                              )
                            )}

                        </div>

                      </div>


                      <div className="flex flex-col gap-3 sm:flex-row">


                        <button

                          onClick={() =>
                            handleOCRConfirmation({})
                          }

                          disabled={isLoading}

                          className="flex-1 rounded-2xl bg-emerald-600 py-4 font-semibold text-white transition hover:bg-emerald-700 disabled:bg-slate-300"

                        >

                          {isLoading
                            ? 'Сохраняем...'
                            : 'Подтвердить данные'}

                        </button>


                        <button

                          onClick={() =>
                            setShowOCREditMode(
                              true
                            )
                          }

                          className="flex-1 rounded-xl border border-slate-300 bg-white py-4 font-semibold text-slate-700 transition hover:bg-[#f7f8fa]"

                        >

                          Редактировать

                        </button>

                      </div>

                    </>

                  ) : (

                    <>


                      <div className="space-y-4">


                        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">

                          Редактирование данных

                        </h3>


                        {stateData.ocr_results.extracted_fields &&

                          Object.entries(
                            stateData.ocr_results.extracted_fields
                          ).map(
                            ([key, value]) => (

                              <div key={key}>


                                <label className="mb-2 block text-sm font-medium capitalize text-slate-700">

                                  {key.replace(
                                    /_/g,
                                    ' '
                                  )}

                                </label>


                                <input

                                  type="text"

                                  defaultValue={
                                    String(value)
                                  }

                                  onChange={(e) => {

                                    setOCREditedData(
                                      (prev) => ({
                                        ...prev,
                                        [key]:
                                          e.target.value
                                      })
                                    )

                                  }}

                                  className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"

                                />

                              </div>

                            )
                          )}

                      </div>


                      <div className="flex flex-col gap-3 sm:flex-row">


                        <button

                          onClick={() => {

                            setShowOCREditMode(
                              false
                            )

                            setOCREditedData(
                              {}
                            )

                          }}

                          className="flex-1 rounded-xl border border-slate-300 bg-white py-4 font-semibold text-slate-700 transition hover:bg-[#f7f8fa]"

                        >

                          ← Вернуться

                        </button>


                        <button

                          onClick={() =>
                            handleOCRConfirmation(
                              ocrEditedData
                            )
                          }

                          disabled={isLoading}

                          className="flex-1 rounded-2xl bg-emerald-600 py-4 font-semibold text-white transition hover:bg-emerald-700 disabled:bg-slate-300"

                        >

                          {isLoading
                            ? 'Сохраняем...'
                            : 'Подтвердить изменения'}

                        </button>

                      </div>

                    </>

                  )}

                </div>

              )}


            {/* Успешное завершение */}

            {stateInfo.inputType === 'success' && (

              <div className="py-8 text-center">


                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100 text-4xl text-emerald-600">

                  ✓

                </div>


                <h2 className="mt-6 text-2xl font-bold text-slate-900">

                  Регистрация завершена

                </h2>


                <p className="mx-auto mt-2 max-w-md leading-7 text-slate-500">

                  Ваши данные успешно переданы администратору отеля.

                  Администратор проверит информацию и подготовит документы для заселения.

                </p>


                <div className="mx-auto mt-8 max-w-md rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-left">


                  <div className="flex items-start gap-3">


                    <div className="mt-0.5 text-lg text-emerald-600">

                      ✓

                    </div>


                    <div>


                      <p className="font-semibold text-emerald-900">

                        Данные получены

                      </p>


                      <p className="mt-1 text-sm leading-6 text-emerald-700">

                        Всё необходимое для оформления заселения отправлено в систему отеля.

                      </p>

                    </div>

                  </div>

                </div>


                {stateData?.generated_documents && (

                  <div className="mt-8 text-left">


                    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">

                      Документы

                    </h2>


                    <div className="space-y-3">


                      {Object.entries(
                        stateData.generated_documents
                      ).map(
                        ([docType, fileId]: [
                          string,
                          any
                        ]) => (

                          <a

                            key={docType}

                            href={`/api/documents/download/file?file_id=${encodeURIComponent(
                              fileId
                            )}&hotel_id=${
                              guest?.hotel_id || ''
                            }`}

                            download

                            className="flex items-center justify-between rounded-xl border border-slate-200/80 bg-white px-4 py-4 transition hover:border-blue-300 hover:bg-blue-50/80"

                          >


                            <div className="flex items-center gap-3">


                              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50/80">

                                📄

                              </div>


                              <div>


                                <p className="font-medium text-slate-900">

                                  {docType === 'договор'
                                    ? 'Договор заселения'
                                    : 'Анкета гостя'}

                                </p>


                                <p className="mt-0.5 text-xs text-slate-500">

                                  PDF-документ

                                </p>

                              </div>

                            </div>


                            <span className="text-blue-600">

                              ↓

                            </span>

                          </a>

                        )
                      )}

                    </div>

                  </div>

                )}


                <div className="mt-8 border-t border-slate-100 pt-6">


                  <p className="text-sm text-slate-500">

                    Если у вас возникли вопросы, обратитесь к администратору отеля.

                  </p>

                </div>

              </div>

            )}

          </div>

        </div>


        <div className="mt-6 flex items-center justify-center gap-2 text-center text-xs text-slate-400">

          <span>
            🔒
          </span>

          <span>

            Данные передаются по защищённому соединению

          </span>

        </div>

      </main>

    </div>

  )

}